import React, { useId, useState } from "react";
import { colors, radius, spacing, typography } from "./tokens";

const labelBaseStyle = {
  display: "block",
  marginBottom: spacing.xs,
  color: colors.textMuted,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
};

const textareaBaseStyle = {
  padding: `${spacing.md}px ${spacing.lg}px`,
  borderRadius: radius.sm,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.text,
  fontSize: typography.size.base,
  lineHeight: typography.lineHeight.form,
  boxSizing: "border-box",
  transition: "border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
  resize: "vertical",
};

const textareaErrorStyle = {
  borderColor: colors.danger,
};

const textareaDisabledStyle = {
  background: colors.disabledBg,
  borderColor: colors.disabledBorder,
  color: colors.disabledText,
  boxShadow: "none",
  cursor: "not-allowed",
  opacity: 1,
};

const errorTextStyle = {
  marginTop: spacing.xs,
  color: colors.danger,
  fontSize: typography.size.xs,
};

function Textarea({
  id,
  label,
  ariaLabel,
  ariaLabelledby,
  error,
  descriptionId,
  style,
  onFocus,
  onBlur,
  ...textareaProps
}) {
  const [isFocused, setIsFocused] = useState(false);
  const fallbackId = useId();
  const textareaId = id || fallbackId;
  const errorId = error ? `${textareaId}-error` : undefined;
  const isDisabled = Boolean(textareaProps.disabled);

  const describedByIds = [descriptionId, errorId].filter(Boolean).join(" ") || undefined;

  const computedAriaLabel =
    !label && !ariaLabel && !ariaLabelledby ? textareaProps.placeholder || undefined : ariaLabel;

  const textareaFocusStyle = isFocused
    ? {
        borderColor: error ? colors.danger : colors.accent,
        boxShadow: `0 0 0 3px ${error ? colors.dangerBorder : colors.focusRing}`,
      }
    : null;

  const mergedTextareaStyle = {
    ...textareaBaseStyle,
    ...(error ? textareaErrorStyle : null),
    ...textareaFocusStyle,
    ...(isDisabled ? textareaDisabledStyle : null),
    ...style,
  };

  return (
    <>
      {label && (
        <label htmlFor={textareaId} style={labelBaseStyle}>
          {label}
        </label>
      )}
      <textarea
        id={textareaId}
        aria-label={computedAriaLabel}
        aria-labelledby={ariaLabelledby}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedByIds}
        style={mergedTextareaStyle}
        onFocus={(event) => {
          setIsFocused(true);
          onFocus && onFocus(event);
        }}
        onBlur={(event) => {
          setIsFocused(false);
          onBlur && onBlur(event);
        }}
        {...textareaProps}
      />
      {error && (
        <div id={errorId} style={errorTextStyle}>
          {error}
        </div>
      )}
    </>
  );
}

export default Textarea;
